import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  FiCalendar,
  FiMapPin,
  FiClock,
  FiChevronDown,
  FiChevronUp,
  FiDownload,
  FiShare2,
  FiArrowLeft,
  FiRepeat,
  FiX,
  FiCheckCircle,
  FiUser,
  FiPhone,
} from 'react-icons/fi';
import { Card, LoaderComponent, EmptyState, Button } from '../components/ui';
import api from '../services/api';
import { QRCodeSVG } from 'qrcode.react';
import { nowCv, fmtParede } from '../utils/tempoCv';

interface Ticket {
  id: number;
  codigo: string;
  qr_code: string;
  participante_nome: string;
  participante_email: string;
  preco: number;
  status: string;
  checkin_at: string | null;
  created_at: string;
  pedido_id: number;
  tipo_nome: string;
  zona_acesso: string;
  evento_id: number;
  evento_titulo: string;
  evento_data: string;
  evento_local: string;
  evento_cidade: string;
  evento_imagem: string;
  pedido_codigo: string;
  source?: 'events' | 'cvma';
}

// evento_data é hora de parede de CV marcada com Z: mostrar tal e qual (UTC).
const formatDate = (dateString: string) => {
  return fmtParede(dateString, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const formatTime = (dateString: string) => {
  return fmtParede(dateString, {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusLabel: Record<string, { text: string; color: string }> = {
  VALID: { text: 'Válido', color: 'bg-green-100 text-green-800' },
  USED: { text: 'Utilizado', color: 'bg-gray-100 text-gray-600' },
  CANCELLED: { text: 'Cancelado', color: 'bg-red-100 text-red-700' },
  RESERVED: { text: 'Reservado', color: 'bg-yellow-100 text-yellow-800' },
  REFUNDED: { text: 'Reembolsado', color: 'bg-orange-100 text-orange-700' },
};

// ─── Transfer Modal ───────────────────────────────────────────────────────────

interface TransferInitData {
  otpId: string;
  otp_destino: string;
  destinatario: { nome: string; email: string };
}

function TransferModal({
  ticket,
  onClose,
  onSuccess,
  showToast,
}: {
  ticket: Ticket;
  onClose: () => void;
  onSuccess: () => void;
  showToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}) {
  const [step, setStep] = useState<'recipient' | 'otp' | 'success'>('recipient');
  const [inputType, setInputType] = useState<'email' | 'phone'>('email');
  const [recipientInput, setRecipientInput] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [initData, setInitData] = useState<TransferInitData | null>(null);
  const [successName, setSuccessName] = useState('');
  const [error, setError] = useState('');

  const handleInitiate = async () => {
    setError('');
    const value = recipientInput.trim();
    if (!value) { setError(inputType === 'email' ? 'Introduz o email' : 'Introduz o telefone'); return; }
    try {
      setLoading(true);
      const body = inputType === 'email'
        ? { destinatario_email: value }
        : { destinatario_telefone: value };
      const { data } = await api.post(`/user-events/tickets/${ticket.id}/transfer/initiate`, body);
      if (!data.success) { setError(data.error || 'Erro ao verificar destinatário'); return; }
      setInitData({ otpId: data.otpId, otp_destino: data.otp_destino, destinatario: data.destinatario });
      setStep('otp');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erro ao verificar destinatário');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setError('');
    if (otpCode.length < 6) { setError('Introduz o código de 6 dígitos'); return; }
    if (!initData) return;
    try {
      setLoading(true);
      const body: any = {
        otp_codigo: otpCode,
        otp_id: initData.otpId,
        ...(inputType === 'email' ? { destinatario_email: recipientInput.trim() } : { destinatario_telefone: recipientInput.trim() }),
      };
      const { data } = await api.post(`/user-events/tickets/${ticket.id}/transfer/confirm`, body);
      if (!data.success) { setError(data.error || 'Código inválido'); return; }
      setSuccessName(data.data?.destinatario?.nome || initData.destinatario.nome);
      setStep('success');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Código inválido ou expirado');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError(''); setOtpCode('');
    try {
      setLoading(true);
      const body = inputType === 'email'
        ? { destinatario_email: recipientInput.trim() }
        : { destinatario_telefone: recipientInput.trim() };
      const { data } = await api.post(`/user-events/tickets/${ticket.id}/transfer/initiate`, body);
      if (data.success) {
        setInitData({ otpId: data.otpId, otp_destino: data.otp_destino, destinatario: data.destinatario });
        showToast('Código reenviado', 'success');
      }
    } catch { showToast('Erro ao reenviar código', 'error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <FiRepeat size={18} className="text-purple-600" />
            <h3 className="text-base font-semibold text-gray-900">
              {step === 'success' ? 'Transferência Concluída' : 'Transferir Pass'}
            </h3>
          </div>
          <button
            onClick={step === 'success' ? () => { onClose(); onSuccess(); } : onClose}
            className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <FiX size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Step 1 — Destinatário */}
        {step === 'recipient' && (
          <div className="p-4 space-y-4">
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-sm font-medium text-gray-900">{ticket.evento_titulo}</p>
              <p className="text-xs text-gray-500 mt-0.5">{ticket.tipo_nome} · #{ticket.codigo}</p>
            </div>

            {/* Email / Telefone toggle */}
            <div className="flex rounded-xl border border-gray-200 overflow-hidden">
              {(['email', 'phone'] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => { setInputType(type); setRecipientInput(''); setError(''); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium transition-colors ${
                    inputType === type
                      ? 'bg-purple-600 text-white'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {type === 'email' ? <FiUser size={14} /> : <FiPhone size={14} />}
                  {type === 'email' ? 'Email' : 'Telefone'}
                </button>
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {inputType === 'email' ? 'Email do destinatário' : 'Telefone do destinatário'}
              </label>
              <input
                type={inputType === 'email' ? 'email' : 'tel'}
                value={recipientInput}
                onChange={(e) => { setRecipientInput(e.target.value); setError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && handleInitiate()}
                placeholder={inputType === 'email' ? 'exemplo@pagali.cv' : '+238 9XX XXX XX'}
                className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              {error && <p className="text-xs text-red-500 mt-1.5">{error}</p>}
            </div>

            <p className="text-xs text-gray-500">
              O destinatário deve ter uma conta Pagali activa. Após a transferência, o Pass ficará na conta dele.
            </p>

            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors">
                Cancelar
              </button>
              <button
                onClick={handleInitiate}
                disabled={loading || !recipientInput.trim()}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'A verificar...' : 'Verificar'}
              </button>
            </div>
          </div>
        )}

        {/* Step 2 — OTP */}
        {step === 'otp' && initData && (
          <div className="p-4 space-y-4">
            <div className="bg-purple-50 rounded-xl p-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center">
                <FiUser size={16} className="text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{initData.destinatario.nome}</p>
                <p className="text-xs text-gray-500">{initData.destinatario.email}</p>
              </div>
            </div>

            <div className="text-center space-y-1">
              <p className="text-sm font-medium text-gray-800">Código de confirmação enviado</p>
              <p className="text-xs text-gray-500">
                Enviámos um código para <span className="font-medium text-gray-700">{initData.otp_destino}</span>
              </p>
            </div>

            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otpCode}
              onChange={(e) => { setOtpCode(e.target.value.replace(/\D/g, '')); setError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
              placeholder="000000"
              className="w-full rounded-xl border border-gray-300 px-3 py-3 text-2xl tracking-[0.5em] text-center font-mono focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            {error && <p className="text-xs text-red-500 text-center">{error}</p>}

            <p className="text-xs text-gray-500 text-center">
              Ao confirmar, o Pass <span className="font-medium">#{ticket.codigo}</span> será transferido para {initData.destinatario.nome} e deixará de estar na tua conta.
            </p>

            <div className="flex gap-3">
              <button onClick={() => { setStep('recipient'); setOtpCode(''); setError(''); }} className="flex-1 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors">
                Voltar
              </button>
              <button
                onClick={handleConfirm}
                disabled={loading || otpCode.length < 6}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'A confirmar...' : 'Confirmar'}
              </button>
            </div>

            <button onClick={handleResend} disabled={loading} className="w-full text-xs text-purple-600 hover:underline text-center py-1 disabled:opacity-50">
              Reenviar código
            </button>
          </div>
        )}

        {/* Step 3 — Sucesso */}
        {step === 'success' && (
          <div className="p-6 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <FiCheckCircle size={32} className="text-green-600" />
            </div>
            <h4 className="text-base font-semibold text-gray-900 mb-2">Pass transferido!</h4>
            <p className="text-sm text-gray-500 mb-6">
              O Pass foi transferido com sucesso para <span className="font-semibold text-gray-800">{successName}</span>.
            </p>
            <button
              onClick={() => { onClose(); onSuccess(); }}
              className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 transition-colors"
            >
              Fechar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MeusBilhetes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [transferTicket, setTransferTicket] = useState<Ticket | null>(null);

  useEffect(() => {
    if (user) loadTickets();
  }, [user]);

  const loadTickets = async () => {
    try {
      setLoading(true);

      // Load both sources in parallel
      const [eventsRes, cvmaRes] = await Promise.allSettled([
        api.get('/user-events/tickets', { params: { limit: 100 } }),
        api.get('/client/bilhetes'),
      ]);

      const eventTickets: Ticket[] =
        eventsRes.status === 'fulfilled' && eventsRes.value.data.success
          ? (eventsRes.value.data.data || []).map((t: any) => ({ ...t, source: 'events' as const }))
          : [];

      const cvmaTickets: Ticket[] =
        cvmaRes.status === 'fulfilled' && cvmaRes.value.data.success
          ? (cvmaRes.value.data.data || []).map((t: any) => ({
              id: t.id,
              source: 'cvma' as const,
              codigo: t.qr_code || String(t.id),
              qr_code: t.qr_code || '',
              participante_nome: '',
              participante_email: '',
              preco: t.valor || 0,
              status: t.status === 'used' || t.usado_em ? 'USED'
                : t.status === 'cancelled' ? 'CANCELLED'
                : 'VALID',
              checkin_at: t.usado_em || null,
              created_at: t.created_at,
              pedido_id: 0,
              tipo_nome: t.titulo || 'Pass',
              zona_acesso: '',
              evento_id: 0,
              evento_titulo: t.evento || t.entidade || 'CVMA',
              evento_data: t.evento_data || t.created_at,
              evento_local: '',
              evento_cidade: '',
              evento_imagem: '',
              pedido_codigo: '',
            }))
          : [];

      // Merge and sort by date desc
      const all = [...eventTickets, ...cvmaTickets].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setTickets(all);
    } catch (err: any) {
      console.error('Error loading tickets:', err);
      showToast('Erro ao carregar Passes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const now = nowCv();
  const upcoming = tickets.filter(
    (t) => t.status === 'VALID' && new Date(t.evento_data) >= now
  );
  const past = tickets.filter(
    (t) => t.status === 'USED' || new Date(t.evento_data) < now
  );
  const display = activeTab === 'upcoming' ? upcoming : past;

  const getQrValue = (t: Ticket) => {
    if (t.qr_code && t.qr_code.startsWith('{')) return t.qr_code;
    if (t.qr_code && !t.qr_code.startsWith('data:')) return t.qr_code;
    return JSON.stringify({ type: 'TICKET', code: t.codigo, event: t.evento_id });
  };

  // Apple Wallet funciona em iOS e macOS (Safari). Chrome/Firefox no Mac não suportam nativamente,
  // mas o ficheiro .pkpass descarrega e pode ser aberto manualmente — mostramos sempre em Apple devices.
  const isAppleDevice = typeof navigator !== 'undefined' && (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (/macintosh/i.test(navigator.userAgent) && /safari/i.test(navigator.userAgent) && !/chrome|chromium|crios/i.test(navigator.userAgent))
  );

  const handleAddToWallet = async (t: Ticket) => {
    try {
      const response = await api.get(`/user-events/tickets/${t.id}/apple-pass`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.apple.pkpass' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `bilhete-${t.codigo}.pkpass`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast('Erro ao gerar passe Apple Wallet', 'error');
    }
  };

  const handleAddToGoogleWallet = async (t: Ticket) => {
    try {
      const { data } = await api.get(`/user-events/tickets/${t.id}/google-pass`);
      if (data.success && data.url) {
        window.open(data.url, '_blank');
      } else {
        showToast('Erro ao gerar passe Google Wallet', 'error');
      }
    } catch {
      showToast('Erro ao gerar passe Google Wallet', 'error');
    }
  };

  const handleShare = async (t: Ticket) => {
    const text = `${t.evento_titulo}\n${formatDate(t.evento_data)} às ${formatTime(t.evento_data)}\nPass: ${t.codigo}`;
    if (navigator.share) {
      try { await navigator.share({ title: t.evento_titulo, text }); } catch {}
    } else {
      await navigator.clipboard.writeText(text);
      showToast('Copiado!', 'success');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <LoaderComponent />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      {/* Transfer Modal */}
      {transferTicket && (
        <TransferModal
          ticket={transferTicket}
          onClose={() => setTransferTicket(null)}
          onSuccess={() => { setTransferTicket(null); loadTickets(); }}
          showToast={showToast}
        />
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/profile')}
          className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
        >
          <FiArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Os Meus Passes</h1>
          <p className="text-sm text-gray-500">{tickets.length} pass(es) no total</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('upcoming')}
          className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold transition-colors ${
            activeTab === 'upcoming'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          🎫 Próximos ({upcoming.length})
        </button>
        <button
          onClick={() => setActiveTab('past')}
          className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold transition-colors ${
            activeTab === 'past'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          📋 Histórico ({past.length})
        </button>
      </div>

      {/* Tickets */}
      {display.length === 0 ? (
        <EmptyState
          icon="🎫"
          title={activeTab === 'upcoming' ? 'Sem Passes próximos' : 'Sem histórico'}
          description={
            activeTab === 'upcoming'
              ? 'Ainda não tens Passes para eventos futuros.'
              : 'Ainda não participaste em nenhum evento.'
          }
        />
      ) : (
        <div className="space-y-4">
          {display.map((t) => {
            const isExpanded = expandedId === t.id;
            const s = statusLabel[t.status] || statusLabel.VALID;

            return (
              <Card key={t.id} className="overflow-hidden">
                {/* Collapsed row */}
                <button
                  onClick={() => setExpandedId(isExpanded ? null : t.id)}
                  className="w-full flex items-center gap-4 p-4 text-left hover:bg-gray-50 transition-colors"
                >
                  {/* Event image */}
                  <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-gray-200">
                    {t.evento_imagem ? (
                      <img
                        src={t.evento_imagem}
                        alt={t.evento_titulo}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl">🎫</div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900 truncate">{t.evento_titulo}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>
                        {s.text}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <FiCalendar size={12} />
                        {formatDate(t.evento_data)}
                      </span>
                      <span className="flex items-center gap-1">
                        <FiClock size={12} />
                        {formatTime(t.evento_data)}
                      </span>
                    </div>
                    <p className="text-xs text-purple-600 font-medium mt-1">{t.tipo_nome || 'Pass'}</p>
                  </div>

                  {isExpanded ? <FiChevronUp size={20} className="text-gray-400" /> : <FiChevronDown size={20} className="text-gray-400" />}
                </button>

                {/* Expanded: QR + details */}
                {isExpanded && (
                  <div className="border-t border-gray-100">
                    {/* QR Code */}
                    <div className="flex flex-col items-center py-6 bg-gray-50">
                      <div className="bg-white rounded-2xl p-4 shadow-sm">
                        <QRCodeSVG
                          value={getQrValue(t)}
                          size={180}
                          level="H"
                          includeMargin
                        />
                      </div>
                      <p className="mt-3 text-sm font-mono font-semibold text-gray-700">{t.codigo}</p>
                      {t.participante_nome && (
                        <p className="text-xs text-gray-500 mt-1">{t.participante_nome}</p>
                      )}
                    </div>

                    {/* Event details */}
                    <div className="px-4 py-3 space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <FiCalendar size={14} className="text-gray-400" />
                        <span>{formatDate(t.evento_data)} às {formatTime(t.evento_data)}</span>
                      </div>
                      {(t.evento_local || t.evento_cidade) && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FiMapPin size={14} className="text-gray-400" />
                          <span>{[t.evento_local, t.evento_cidade].filter(Boolean).join(' · ')}</span>
                        </div>
                      )}
                      {t.checkin_at && (
                        <div className="flex items-center gap-2 text-sm text-green-600">
                          <span>✓ Check-in: {new Date(t.checkin_at).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}</span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap gap-2">
                      <button
                        onClick={() => handleShare(t)}
                        className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                      >
                        <FiShare2 size={14} /> Partilhar
                      </button>
                      {t.source === 'events' && t.status === 'VALID' && new Date(t.evento_data) > nowCv() && (
                        <button
                          onClick={() => setTransferTicket(t)}
                          className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                        >
                          <FiRepeat size={14} /> Transferir
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/eventos`)}
                        className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 transition-colors"
                      >
                        Ver Evento
                      </button>
                      {isAppleDevice && t.source === 'events' && t.status === 'VALID' && (
                        <button
                          onClick={() => handleAddToWallet(t)}
                          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white bg-black hover:bg-gray-900 transition-colors"
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                          Adicionar ao Apple Wallet
                        </button>
                      )}
                      {!isAppleDevice && t.source === 'events' && t.status === 'VALID' && (
                        <button
                          onClick={() => handleAddToGoogleWallet(t)}
                          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#1A73E8] hover:bg-[#1557B0] transition-colors"
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                          Guardar no Google Wallet
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Info */}
      {display.length > 0 && (
        <div className="mt-8 p-4 rounded-xl bg-purple-50 border border-purple-100">
          <h4 className="font-semibold text-purple-900 text-sm mb-2">Como usar o teu bilhete</h4>
          <ol className="text-xs text-purple-700 space-y-1 list-decimal list-inside">
            <li>Apresenta o QR code na entrada do evento</li>
            <li>Podes fazer screenshot ou partilhar o bilhete</li>
            <li>Cada bilhete só pode ser usado uma vez</li>
          </ol>
        </div>
      )}
    </div>
  );
}
